import React, { useState } from 'react';
import { Card, CardContent } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Badge } from '@vaiu/ui/badge';
import { 
  Zap,
  ShoppingBag,
  CreditCard,
  Package,
  HeadphonesIcon,
  Users,
  RefreshCw,
  Instagram,
  Youtube,
  Globe,
  MousePointer,
  Link,
  ArrowRight,
  Sparkles,
  ShoppingCart,
  X,
  CheckCircle,
  Rocket,
  Share2,
  MessageCircle,
  BarChart3,
  Clock,
  Shield,
  Smartphone,
  Code,
  DollarSign
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getDomainInfo } from '@/utils/domainDetection';

const AtomicCommerceSection: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeDemo, setActiveDemo] = useState<'instagram' | 'youtube' | 'google' | 'wordpress'>('instagram');

  // Get the app domain/name for the WordPress link
  const getTokenCreationLink = () => {
    // Check if embedded (iframe or embed param)
    const searchParams = new URLSearchParams(location.search);
    const isEmbedded = searchParams.get('embed') === 'true' || window.self !== window.top;
    
    if (isEmbedded) {
      return 'atomicMarketplace.com';
    }
    
    // Otherwise use current hostname
    const domainInfo = getDomainInfo();
    return window.location.hostname;
  };

  const atomicComponents = [
    { icon: CreditCard, label: "Payment", color: "text-green-600" },
    { icon: ShoppingBag, label: "Order", color: "text-blue-600" },
    { icon: Package, label: "Delivery", color: "text-purple-600" },
    { icon: HeadphonesIcon, label: "Support", color: "text-orange-600" },
    { icon: Users, label: "CRM", color: "text-pink-600" },
    { icon: RefreshCw, label: "Reorder", color: "text-indigo-600" }
  ];

  const platforms = [
    { 
      id: 'instagram',
      icon: Instagram, 
      name: "Instagram",
      description: "Tell buyers to DM you a token",
      example: "Share a product photo → Customer clicks link → Purchase complete. No website needed."
    },
    { 
      id: 'youtube',
      icon: Youtube, 
      name: "YouTube",
      description: "Tell buyers to DM you a token",
      example: "Video description link → One-click purchase → Instant delivery. No cart abandonment."
    },
    { 
      id: 'google',
      icon: MousePointer, 
      name: "Google Ads",
      description: "Advertise and sell in one click",
      example: "Customer clicks ad → Payment processed → Order fulfilled. Skip the website entirely."
    },
    { 
      id: 'wordpress',
      icon: Globe, 
      name: "WordPress",
      description: "", // Will be set dynamically
      example: "Embed token anywhere → Instant checkout → Complete transaction. No plugins required."
    }
  ];

  const traditionalVsAtomic = {
    traditional: [
      "Browse products",
      "Add to cart",
      "Create account",
      "Enter shipping info",
      "Enter payment info",
      "Confirm order",
      "Wait for confirmation",
      "Track separately",
      "Contact support separately",
      "Manual reorder"
    ],
    atomic: [
      "Click/Scan token",
      "Purchase complete ✨"
    ]
  };

  const benefits = [
    {
      icon: Rocket,
      title: "95% Faster Checkout",
      description: "From discovery to purchase in one action"
    },
    {
      icon: BarChart3,
      title: "3x Conversion Rate",
      description: "No cart abandonment, no form fatigue"
    },
    {
      icon: DollarSign,
      title: "70% Lower Costs",
      description: "No website, no platform fees, no complexity"
    },
    {
      icon: Clock,
      title: "Instant Everything",
      description: "Payment, fulfillment, and support in one token"
    }
  ];

  const tokenFeatures = [
    {
      title: "Self-Contained Commerce",
      description: "Each token is a complete transaction waiting to happen",
      features: [
        "Product details embedded",
        "Pricing locked in",
        "Shipping calculated",
        "Taxes included"
      ]
    },
    {
      title: "Smart Lifecycle",
      description: "Tokens that evolve with the customer journey",
      features: [
        "Pre-purchase: Discovery & pricing",
        "Purchase: Instant execution",
        "Post-purchase: Support & tracking",
        "Future: Reorder & referrals"
      ]
    },
    {
      title: "Universal Compatibility",
      description: "Works everywhere, no integration needed",
      features: [
        "QR codes for physical",
        "Links for digital",
        "NFC for proximity",
        "API for platforms"
      ]
    }
  ];

  return (
    <div className="py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white via-blue-50/30 to-white">
      {/* CTA Section - Moved to Top */}
      <div className="max-w-4xl mx-auto text-center mb-12">
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold mb-3 text-gray-900">
              Start Selling in One Click
            </h2>
            <p className="text-xl mb-6 text-gray-600">
              Accept your first payment in seconds. 
              No website, no complexity, just instant commerce.
            </p>
            
            <Button 
              size="lg" 
              onClick={() => navigate('/signup')}
              className="bg-gray-900 text-white hover:bg-gray-800 px-8 py-3 text-lg font-semibold inline-flex items-center justify-center min-w-fit whitespace-nowrap"
            >
              <Sparkles className="w-5 h-5 mr-2 flex-shrink-0" />
              <span>Start getting paid</span>
            </Button>
            <p className="text-sm text-gray-500 mt-4">
              Free to start • No credit card required • Setup in seconds
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Hero Section */}
      <div className="max-w-6xl mx-auto text-center mb-12">
        <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4">
          Atomic Commerce
        </h1>
        
        <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-3">
          One token. Complete transaction. Cart-free commerce.
        </p>
        
        <p className="text-lg text-gray-500 max-w-2xl mx-auto mb-8">
          A single token carries payment, order, delivery, support, CRM, and reorder capability. 
          Sell anywhere with one link. Advertise and transact in one step.
        </p>

        {/* Atomic Token Visualization */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold mb-4">One Token, Everything Included</h3>
          
          <div className="flex flex-wrap justify-center items-center gap-4 mb-6">
            {atomicComponents.map((component, index) => (
              <div key={index} className="relative">
                <div className="flex flex-col items-center">
                  <div className={`w-14 h-14 bg-gradient-to-br from-gray-50 to-gray-100 rounded-full flex items-center justify-center border-2 border-gray-200 ${component.color}`}>
                    <component.icon className="w-7 h-7" />
                  </div>
                  <span className="text-xs mt-1 font-medium">{component.label}</span>
                </div>
                {index < atomicComponents.length - 1 && (
                  <div className="absolute top-8 -right-2 text-gray-400">
                    <span className="text-2xl">+</span>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="flex justify-center">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-full font-bold text-lg shadow-lg">
              = Atomic Commerce Token
            </div>
          </div>
        </div>
      </div>

      {/* Traditional vs Atomic */}
      <div className="max-w-6xl mx-auto mb-12">
        <h2 className="text-3xl font-bold text-center mb-8">
          Skip the Shopping Cart Forever
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-red-200 bg-red-50/50">
            <CardContent className="p-6">
              <div className="flex items-center mb-4">
                <ShoppingCart className="w-8 h-8 text-red-600 mr-3" />
                <h3 className="text-xl font-bold">Traditional E-Commerce</h3>
              </div>
              <div className="space-y-2">
                {traditionalVsAtomic.traditional.map((step, index) => (
                  <div key={index} className="flex items-center text-sm">
                    <span className="w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-xs mr-3">
                      {index + 1}
                    </span>
                    <span className="text-gray-700">{step}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-center">
                <Badge variant="destructive">10+ Steps, 70% Cart Abandonment</Badge>
              </div>
            </CardContent>
          </Card>

          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="p-6">
              <div className="flex items-center mb-4">
                <Zap className="w-8 h-8 text-green-600 mr-3" />
                <h3 className="text-xl font-bold">Atomic Commerce</h3>
              </div>
              <div className="space-y-4">
                {traditionalVsAtomic.atomic.map((step, index) => (
                  <div key={index} className="flex items-center">
                    <span className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center mr-3">
                      <CheckCircle className="w-5 h-5" />
                    </span>
                    <span className="text-lg font-medium text-gray-700">{step}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 p-4 bg-white rounded-lg">
                <p className="text-sm text-gray-600">
                  Everything happens instantly: payment processing, order creation, 
                  inventory update, shipping label, CRM entry, and support ticket ready.
                </p>
              </div>
              <div className="mt-4 text-center">
                <Badge className="bg-green-600 text-white">1 Click, 0% Abandonment</Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Platform Integration */}
      <div className="max-w-6xl mx-auto mb-12">
        <h2 className="text-3xl font-bold text-center mb-3">
          Sell Everywhere, Instantly
        </h2>
        <p className="text-xl text-gray-600 text-center mb-8 max-w-3xl mx-auto">
          Transform any platform into a direct sales channel. No website required.
        </p>
        
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {platforms.map((platform) => (
              <Button
                key={platform.id}
                variant={activeDemo === platform.id ? "default" : "outline"}
                onClick={() => setActiveDemo(platform.id as any)}
                className="flex items-center gap-2"
              >
                <platform.icon className="w-5 h-5" />
                {platform.name}
              </Button>
            ))}
          </div>

          <Card className="border-2 border-blue-200">
            <CardContent className="p-8">
              {platforms.find(p => p.id === activeDemo) && (() => {
                const activePlatform = platforms.find(p => p.id === activeDemo)!;
                const IconComponent = activePlatform.icon;
                return (
                  <div className="space-y-4">
                    <div className="flex items-center justify-center mb-6">
                      {IconComponent && (
                        <IconComponent className="w-12 h-12 text-blue-600 mr-4" />
                      )}
                      <h3 className="text-2xl font-bold">
                        {activePlatform.name}
                      </h3>
                    </div>
                    <p className="text-lg text-center text-gray-700 mb-4">
                      {activePlatform.id === 'wordpress' ? (
                        <>
                          Tell buyers to email you a token after creating one{' '}
                          <a 
                            href={`https://${getTokenCreationLink()}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline font-medium"
                          >
                            (here)
                          </a>
                        </>
                      ) : (
                        activePlatform.description
                      )}
                    </p>
                    {activePlatform.id === 'instagram' ? (
                      <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-gray-200 rounded-lg p-6 max-w-md mx-auto">
                        {/* Instagram DM Header */}
                        <div className="bg-white border-b border-gray-200 px-4 py-3 rounded-t-lg flex items-center space-x-3 mb-4">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold">
                            A
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-gray-900">artisan_crafts</p>
                            <p className="text-xs text-gray-500">Active now</p>
                          </div>
                        </div>
                        
                        {/* DM Messages */}
                        <div className="space-y-3 px-2">
                          {/* Customer message */}
                          <div className="flex justify-end">
                            <div className="bg-blue-500 text-white rounded-2xl rounded-tr-sm px-4 py-2 max-w-[80%]">
                              <p className="text-sm mt-2">I love the gloves. Please send me the green ones in size M. ABC123 for $49</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : activePlatform.id === 'youtube' ? (
                      <div className="bg-white border-2 border-gray-200 rounded-lg p-6 max-w-2xl mx-auto">
                        {/* YouTube Channel Comment/Description Style */}
                        <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 rounded-t-lg flex items-center space-x-3">
                          <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center text-white font-bold">
                            Y
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">Artisan Crafts Channel</p>
                            <p className="text-xs text-gray-500">Channel owner</p>
                          </div>
                        </div>
                        <div className="px-4 py-6 bg-white">
                          <p className="text-sm text-gray-700 leading-relaxed">
                            To order from me, generate your purchase token at vaiu.app; you'll get something like "ABC123"; then email me your token at artisan@handmadecrafts.com
                          </p>
                        </div>
                      </div>
                    ) : activePlatform.id === 'wordpress' ? (
                      <div className="bg-white border-2 border-gray-200 rounded-lg p-6 max-w-2xl mx-auto">
                        <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 rounded-t-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                                S
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-900">Sarah Chen</p>
                                <p className="text-xs text-gray-500">sarah.chen@email.com</p>
                              </div>
                            </div>
                            <span className="text-xs text-gray-500">Today, 2:34 PM</span>
                          </div>
                          <div className="text-xs text-gray-500">
                            To: artisan@handmadecrafts.com
                          </div>
                        </div>
                        <div className="px-4 py-6 bg-white">
                          <p className="text-sm text-gray-700">
                            ABC123 for $89 for the wooden fire engine and ladder.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-blue-50 rounded-lg p-6">
                        <p className="text-center text-gray-600">
                          <strong>Example:</strong> {activePlatform.example}
                        </p>
                      </div>
                    )}
                    <div className="flex justify-center mt-6">
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">No website needed</span>
                    </div>
                    <div className="flex items-center gap-2 text-green-600 ml-6">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">Instant setup</span>
                    </div>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Token Features */}
      <div className="max-w-6xl mx-auto mb-12">
        <h2 className="text-3xl font-bold text-center mb-8">
          Smart Commerce Tokens
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {tokenFeatures.map((feature, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-gray-600 mb-4">{feature.description}</p>
                <ul className="space-y-2">
                  {feature.features.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Benefits Grid */}
      <div className="max-w-6xl mx-auto mb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, index) => (
            <Card key={index} className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <benefit.icon className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="font-bold text-lg mb-2">{benefit.title}</h3>
                <p className="text-sm text-gray-600">{benefit.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Use Cases */}
      <div className="max-w-6xl mx-auto mb-12">
        <h2 className="text-3xl font-bold text-center mb-8">
          Perfect For Every Seller
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            {
              title: "Content Creators",
              description: "Monetize your audience instantly",
              items: ["YouTube videos", "Instagram posts", "TikTok links", "Podcast episodes"]
            },
            {
              title: "Small Businesses",
              description: "Sell without a website",
              items: ["Local services", "Handmade products", "Digital goods", "Consultations"]
            },
            {
              title: "Enterprises",
              description: "Streamline B2B transactions",
              items: ["Quote-to-cash", "Procurement", "Subscriptions", "Licensing"]
            },
            {
              title: "Events & Tickets",
              description: "Instant admission and access",
              items: ["Concert tickets", "Conference passes", "Workshops", "Experiences"]
            },
            {
              title: "Digital Products",
              description: "Immediate delivery",
              items: ["E-books", "Courses", "Software", "Templates"]
            },
            {
              title: "Services",
              description: "Book and pay in one action",
              items: ["Appointments", "Consultations", "Repairs", "Deliveries"]
            }
          ].map((useCase, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-2">{useCase.title}</h3>
                <p className="text-sm text-gray-600 mb-4">{useCase.description}</p>
                <ul className="space-y-1">
                  {useCase.items.map((item, idx) => (
                    <li key={idx} className="text-sm text-gray-700">• {item}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AtomicCommerceSection;